import React, { useState, useEffect } from "react"
import axios from "axios"
import { archiveUrl } from "../../../Config/config"

const ArchiveContent = ({ yearType, selectedYear, loading: externalLoading }) => {
  const [archiveData, setArchiveData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    const fetchArchiveData = async () => {
      if (!selectedYear) return

      setLoading(true)
      setError(null)

      try {
        let url
        if (yearType === "cy") {
          url = `${archiveUrl}/league-table/archive/${selectedYear}`
        } else {
          // For fiscal year (April to March)
          const fiscalYearEnd = selectedYear + 1
          url = `${archiveUrl}/league-table/archive/${selectedYear}-${fiscalYearEnd}`
        }

        console.log("Fetching archive data from:", url)
        const response = await axios.get(url)
        setArchiveData(response.data)
      } catch (err) {
        console.error("Error fetching archive data:", err)
        setError("Failed to load archive data. Please try again later.")
      } finally {
        setLoading(false)
      }
    }

    fetchArchiveData()
  }, [yearType, selectedYear])

  const handleImageClick = () => {
    if (archiveData?.data?.link) {
      window.open(archiveData.data.link, "_blank")
    }
  }

  // Helper to get attachments for a specific item with their links
  const getItemAttachments = (item) => {
    if (!Array.isArray(item.attachments)) return []
    return item.attachments.map((att) => ({
      ...att,
      link: att.link || item.link || null,
    }))
  }

  // Function to render images in rows of 2 for a single group
  const renderGroupImages = (attachments) => {
    const totalImages = attachments.length
    const totalRows = Math.ceil(totalImages / 2)

    return (
      <div className="space-y-6">
        {Array.from({ length: totalRows }).map((_, rowIndex) => {
          const startIdx = rowIndex * 2
          const imagesInThisRow = attachments.slice(startIdx, startIdx + 2)
          const isSingleImageInRow = imagesInThisRow.length === 1

          return (
            <div
              key={`row-${rowIndex}`}
              className={`grid gap-12 ${
                isSingleImageInRow ? "grid-cols-1" : "grid-cols-1 md:grid-cols-2"
              } max-w-4xl w-full mx-auto`}
            >
              {imagesInThisRow.map((item, index) => (
                <div
                  key={`image-${startIdx + index}`}
                  className={`rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300 cursor-pointer ${
                    isSingleImageInRow ? "mx-auto md:w-1/2" : "w-full"
                  }`}
                  onClick={() => {
                    if (item.link) window.open(item.link, "_blank")
                  }}
                >
                  <img
                    src={item.file_path || "/placeholder.svg"}
                    alt={`Archive item ${startIdx + index + 1}`}
                    className="w-full h-auto object-cover"
                    onError={(e) => {
                      e.target.src = "https://via.placeholder.com/400x300?text=Image+Not+Available"
                    }}
                  />
                </div>
              ))}
            </div>
          )
        })}
      </div>
    )
  }

  if (loading || externalLoading) {
    return (
      <div className="flex justify-center items-center p-12">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#bc8b20]"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6 bg-red-50 rounded-lg text-red-700">
        <p>{error}</p>
      </div>
    )
  }

  if (!archiveData || !archiveData.data || !Array.isArray(archiveData.data)) {
    return (
      <div className="p-6 bg-gray-100 rounded-lg">
        <p className="text-lg text-gray-600">No archive data is available for the selected period.</p>
      </div>
    )
  }

  // Filter out items that don't have attachments
  const itemsWithAttachments = archiveData.data.filter(item => 
    Array.isArray(item.attachments) && item.attachments.length > 0
  )

  if (itemsWithAttachments.length === 0) {
    return (
      <div className="p-6 bg-gray-100 rounded-lg">
        <p className="text-lg text-gray-600">No archive data is available for the selected period.</p>
      </div>
    )
  }

  return (
    <div className="p-6 bg-white rounded-lg shadow w-[75%] mx-auto">
      {/* <h3 className="text-xl font-semibold mb-6 text-[#333] text-center">
        {yearType === 'cy' ? `Calendar Year ${selectedYear}` : `Financial Year ${selectedYear}-${selectedYear + 1}`} Archives
      </h3> */}

      <div className="space-y-8">
        {itemsWithAttachments.map((item, groupIndex) => {
          const attachments = getItemAttachments(item)
          
          return (
            <React.Fragment key={`group-${groupIndex}`}>
              {/* Optional: Display group header if needed */}
              {item.header && (
                <div className="text-center mb-4">
                  <h4 className="text-lg font-semibold text-[#333]">{item.header}</h4>
                </div>
              )}
              
              {/* Render images for this group */}
              {renderGroupImages(attachments)}
              
              {/* Divider line between groups (except after the last group) */}
              {groupIndex < itemsWithAttachments.length - 1 && (
                <div className="w-full max-w-2xl mx-auto pt-4">
                  <hr className="border-t border-gray-400" />
                </div>
              )}
            </React.Fragment>
          )
        })}
      </div>
    </div>
  )
}

export default ArchiveContent